import { useEffect, useMemo, useRef, useState, type CSSProperties, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  careerDuration,
  emptyProfile,
  type EmployeeProfile,
  type ProfileCareer,
  type ProfileCertificate,
  type ProfileEducation,
  type ProfileProject,
  type ProfilePersonal,
  BLOOD_TYPES,
  MBTI_DESCRIPTIONS,
  MBTI_QUESTIONS,
  scoreMbti,
} from '@dxtech/shared';
import { api, type ProfileBasic } from '../api/client.js';
import { useApp } from '../state/app.js';
import { Modal, ModalFrame, Spinner } from '../ui/common.js';

const COMPANY = '주식회사 디엑스테크';

// API 가 기본 정보에 함께 내려주는 생일 부가 정보(api/client.ts 의 ProfileBasic 확장)
type Basic = ProfileBasic & { birthdayType?: string; birthdayThisYear?: string };
/** '1985-05-12' + 음력 → '1985.05.12 (음력)' */
const birthdayText = (b: Basic | null): string =>
  b?.birthday ? `${b.birthday.slice(0, 10).replace(/-/g, '.')}${b.birthdayType ? ` (${b.birthdayType})` : ''}` : '';
/** 올해 양력 생일 기준 카운트다운 — 'D-12' / '오늘' / '지남' */
function birthdayCountdown(thisYear?: string): { label: string; tone: 'soon' | 'today' | 'past' | 'later' } | null {
  if (!thisYear) return null;
  const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
  const days = Math.round((Date.parse(`${thisYear}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
  const wd = ['일', '월', '화', '수', '목', '금', '토'][new Date(`${thisYear}T00:00:00Z`).getUTCDay()];
  const md = `${Number(thisYear.slice(5, 7))}/${Number(thisYear.slice(8, 10))}(${wd})`;
  if (days === 0) return { label: `오늘 생일 ${md}`, tone: 'today' };
  if (days < 0) return { label: `올해 생일 ${md} · 지남`, tone: 'past' };
  return { label: `올해 생일 ${md} · D-${days}`, tone: days <= 30 ? 'soon' : 'later' };
}

/** 파일명용 타임스탬프 (KST) → 'YYYYMMDD_HHmmss' */
function stampNow(): string {
  const s = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(new Date());
  return s.replace(/[-:]/g, '').replace(' ', '_');
}

/** 범용 편집 테이블 — 섹션별 다중 행 CRUD */
function EditTable<T extends object>({ title, columns, rows, blank, onChange, readOnly }: {
  title: string;
  columns: { key: keyof T & string; label: string; flex: number }[];
  rows: T[];
  blank: () => T;
  onChange: (rows: T[]) => void;
  readOnly: boolean;
}): JSX.Element {
  const grid = columns.map((c) => `${c.flex}fr`).join(' ') + (readOnly ? '' : ' 74px');
  const set = (i: number, key: keyof T & string, v: string): void => {
    const next = rows.slice();
    next[i] = { ...next[i], [key]: v };
    onChange(next);
  };
  // 행 이동(순서 변경): i번 행을 dir(-1=위/+1=아래)로 스왑
  const move = (i: number, dir: -1 | 1): void => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const val = (row: T, key: keyof T & string): string => String((row[key] as unknown) ?? '');
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>● {title}</div>
        {!readOnly && (
          <button className="dx-btn dx-btn-soft" style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12 }}
            onClick={() => onChange([...rows, blank()])}>+ 행 추가</button>
        )}
      </div>
      <div style={{ border: '1px solid #EDEFF2', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: grid, background: '#F9FAFB', borderBottom: '1px solid #EDEFF2', fontSize: 11.5, fontWeight: 700, color: '#6B7280' }}>
          {columns.map((c) => <div key={c.key} style={{ padding: '7px 8px' }}>{c.label}</div>)}
          {!readOnly && <div style={{ padding: '7px 8px', textAlign: 'center' }}>순서</div>}
        </div>
        {rows.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#C0C4CB', fontSize: 12.5 }}>등록된 내용이 없습니다.</div>
        ) : rows.map((row, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: grid, borderBottom: '1px solid #F6F7F9', alignItems: 'center' }}>
            {columns.map((c) => (
              <div key={c.key} style={{ padding: '4px 6px' }}>
                {readOnly ? (
                  <span style={{ fontSize: 12.5, color: '#374151' }}>{val(row, c.key) || '—'}</span>
                ) : (
                  <input className="dx-input" style={{ width: '100%', padding: '6px 8px', fontSize: 12.5 }}
                    value={val(row, c.key)} onChange={(e) => set(i, c.key, e.target.value)} />
                )}
              </div>
            ))}
            {!readOnly && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <button title="위로" disabled={i === 0} onClick={() => move(i, -1)}
                  style={{ border: 'none', background: 'transparent', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? '#D1D5DB' : '#6B7280', fontSize: 12, lineHeight: 1, padding: '2px 3px' }}>▲</button>
                <button title="아래로" disabled={i === rows.length - 1} onClick={() => move(i, 1)}
                  style={{ border: 'none', background: 'transparent', cursor: i === rows.length - 1 ? 'default' : 'pointer', color: i === rows.length - 1 ? '#D1D5DB' : '#6B7280', fontSize: 12, lineHeight: 1, padding: '2px 3px' }}>▼</button>
                <button title="행 삭제" onClick={() => onChange(rows.filter((_, j) => j !== i))}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#DC2626', fontSize: 15, lineHeight: 1, padding: '2px 3px' }}>×</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProfileModal({ myUpn, isAdmin, people, initialTarget, onClose }: {
  myUpn: string; myName?: string; isAdmin: boolean; people: { upn: string; name: string }[]; initialTarget?: string; onClose: () => void;
}): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  // 대상은 팝업을 연 임직원으로 고정(선택 불가). 본인 또는 관리자만 편집·삭제·저장 가능
  const target = initialTarget ?? myUpn;
  const [draft, setDraft] = useState<EmployeeProfile & ProfilePersonal>(emptyProfile());
  const [quiz, setQuiz] = useState(false);
  const isMine = target.toLowerCase() === myUpn.toLowerCase();
  const canEdit = isMine || isAdmin;

  const q = useQuery({ queryKey: ['profile', target], queryFn: () => api.profile(isMine ? undefined : target) });
  const basic: Basic | null = (q.data?.basic as Basic | null) ?? null;
  const bday = birthdayCountdown(basic?.birthdayThisYear);

  // 서버 데이터 → 편집 draft 동기화 (대상별 최초 1회만; 이후 refetch가 로컬 편집/삭제를 덮어쓰지 않도록)
  const syncedTarget = useRef<string | null>(null);
  useEffect(() => {
    if (!q.data) return;
    if (syncedTarget.current === target) return;
    syncedTarget.current = target;
    setDraft(q.data.profile ?? emptyProfile());
  }, [q.data, target]);

  const save = useMutation({
    mutationFn: () => api.saveProfile({ ...draft, upn: isMine ? undefined : target }),
    onSuccess: () => { flash('프로파일을 저장했습니다'); qc.invalidateQueries({ queryKey: ['profile', target] }); qc.invalidateQueries({ queryKey: ['personal'] }); },
    onError: (e) => flash(e instanceof Error ? e.message : '저장 실패'),
  });

  const targetName = useMemo(() => people.find((p) => p.upn === target)?.name ?? basic?.name ?? '', [people, target, basic]);

  const downloadPdf = (): void => {
    if (!basic) { flash('기본 정보를 불러오지 못했습니다'); return; }
    const html = buildProfileHtml({ ...basic, birthday: birthdayText(basic) }, draft, `${targetName || '직원'}_${stampNow()}`);
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) { flash('팝업이 차단되었습니다. 팝업을 허용해 주세요.'); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const downloadExcel = (): void => {
    if (!basic) { flash('기본 정보를 불러오지 못했습니다'); return; }
    const html = buildProfileExcelHtml({ ...basic, birthday: birthdayText(basic) }, draft, targetName);
    // UTF-8 BOM + ms-excel MIME → Excel이 한글 그대로 인식, HTML 테이블을 시트로 표시
    const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `프로파일_${targetName || '직원'}_${stampNow()}.xls`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Modal width={940} onClose={onClose}>
      <div style={{ padding: 22, maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>개인 프로파일</div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 999, padding: '3px 12px' }}>
            {targetName || basic?.name || target}{isMine ? ' · 본인' : ''}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="dx-btn dx-btn-ghost" style={{ fontSize: 13 }} onClick={downloadPdf}>PDF 다운로드</button>
            <button className="dx-btn dx-btn-ghost" style={{ fontSize: 13 }} onClick={downloadExcel}>Excel 다운로드</button>
            {canEdit && <button className="dx-btn dx-btn-primary" style={{ fontSize: 13 }} disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? '저장 중…' : '저장'}</button>}
            <button title="닫기" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 30, lineHeight: 1, marginLeft: 2 }}>×</button>
          </div>
        </div>

        {q.isLoading ? (
          <div style={{ padding: 50, textAlign: 'center' }}><Spinner /></div>
        ) : (
          <>
            {/* 기본 정보 */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 8 }}>● 기본 정보</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', background: '#EDEFF2', border: '1px solid #EDEFF2', borderRadius: 8, overflow: 'hidden' }}>
                <Info k="성명" v={basic?.name} />
                <Info k="소속" v={COMPANY} />
                <Info k="직위" v={basic?.title} />
                <div style={{ background: '#fff', padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#6B7280', width: 56, flexShrink: 0 }}>생년월일</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12.5, color: '#374151' }}>{birthdayText(basic) || '—'}</span>
                    {bday && (
                      <span style={{ display: 'inline-block', marginTop: 2, fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '1px 6px',
                        ...(bday.tone === 'today' ? { background: '#FEF3C7', color: '#B45309' } : bday.tone === 'soon' ? { background: '#EFF4FE', color: '#1D4ED8' } : { background: '#F3F4F6', color: '#6B7280' }) }}>
                        {bday.label}
                      </span>
                    )}
                  </span>
                </div>
                <Info k="이메일" v={basic?.email} />
                <Info k="연락처" v={basic?.phone} />
                <div style={{ background: '#fff', padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#6B7280', width: 56, flexShrink: 0 }}>경력기간</span>
                  <span style={{ fontSize: 12.5 }}>{careerDuration(draft.careers) || '—'}</span>
                  <span style={{ fontSize: 10.5, color: '#9CA3AF', marginLeft: 'auto' }}>근무 경력 자동 산정</span>
                </div>
                <div style={{ background: '#fff', padding: '8px 10px', gridColumn: 'span 2', display: 'flex', gap: 8 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#6B7280', width: 40, flexShrink: 0 }}>주소</span>
                  <span style={{ fontSize: 12.5 }}>{basic?.address || '—'}</span>
                </div>
                <div style={{ background: '#fff', padding: '6px 10px', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#6B7280', width: 56, flexShrink: 0 }}>혈액형</span>
                  {canEdit ? (
                    <select className="dx-select" style={{ height: 30, fontSize: 12.5 }} value={draft.bloodType ?? ''}
                      onChange={(e) => setDraft({ ...draft, bloodType: e.target.value as ProfilePersonal['bloodType'] })}>
                      <option value="">선택 안 함</option>
                      {BLOOD_TYPES.map((t) => <option key={t} value={t}>{t}형</option>)}
                    </select>
                  ) : <span style={{ fontSize: 12.5, color: '#374151' }}>{draft.bloodType ? `${draft.bloodType}형` : '—'}</span>}
                </div>
                <div style={{ background: '#fff', padding: '6px 10px', gridColumn: 'span 2', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#6B7280', width: 40, flexShrink: 0 }}>MBTI</span>
                  {canEdit ? (
                    <>
                      <select className="dx-select" style={{ height: 30, fontSize: 12.5 }} value={draft.mbti ?? ''}
                        onChange={(e) => setDraft({ ...draft, mbti: e.target.value })}>
                        <option value="">선택 안 함</option>
                        {Object.keys(MBTI_DESCRIPTIONS).sort().map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <button className="dx-btn dx-btn-ghost" style={{ fontSize: 12, height: 30, padding: '0 10px' }} onClick={() => setQuiz(true)}>확인하기</button>
                    </>
                  ) : <span style={{ fontSize: 12.5, color: '#374151' }}>{draft.mbti || '—'}</span>}
                  {draft.mbti && MBTI_DESCRIPTIONS[draft.mbti] && <span style={{ fontSize: 11.5, color: '#6B7280' }}>{MBTI_DESCRIPTIONS[draft.mbti]}</span>}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 5 }}>기본 정보(성명·직위·생년월일·연락처·주소 등)는 임직원 정보/내 정보에서 관리됩니다. 혈액형·MBTI는 여기서 입력하고 저장하세요.</div>
            </div>

            <EditTable<ProfileEducation> title="학력 사항" rows={draft.educations} readOnly={!canEdit}
              blank={() => ({ school: '', period: '', major: '', minor: '', note: '' })}
              onChange={(educations) => setDraft({ ...draft, educations })}
              columns={[{ key: 'school', label: '학교명', flex: 2 }, { key: 'period', label: '재학 기간', flex: 2 }, { key: 'major', label: '전공', flex: 2 }, { key: 'minor', label: '부전공', flex: 1.5 }, { key: 'note', label: '비고', flex: 2 }]} />

            <EditTable<ProfileCareer> title="근무 경력사항" rows={draft.careers} readOnly={!canEdit}
              blank={() => ({ company: '', period: '', position: '', duty: '', note: '' })}
              onChange={(careers) => setDraft({ ...draft, careers })}
              columns={[{ key: 'company', label: '근무처', flex: 2 }, { key: 'period', label: '근무 기간', flex: 2 }, { key: 'position', label: '직위', flex: 1.2 }, { key: 'duty', label: '수행 업무', flex: 2.5 }, { key: 'note', label: '비고', flex: 1.5 }]} />

            <EditTable<ProfileCertificate> title="자격증" rows={draft.certificates} readOnly={!canEdit}
              blank={() => ({ name: '', issuer: '', acquiredDate: '', note: '' })}
              onChange={(certificates) => setDraft({ ...draft, certificates })}
              columns={[{ key: 'name', label: '자격증', flex: 2 }, { key: 'issuer', label: '발행처', flex: 2 }, { key: 'acquiredDate', label: '취득일자', flex: 1.5 }, { key: 'note', label: '비고', flex: 2 }]} />

            <EditTable<ProfileProject> title="업무 경력사항" rows={draft.projects} readOnly={!canEdit}
              blank={() => ({ name: '', period: '', role: '', client: '', tech: '' })}
              onChange={(projects) => setDraft({ ...draft, projects })}
              columns={[{ key: 'name', label: '사업명', flex: 2.5 }, { key: 'period', label: '참여 기간', flex: 2 }, { key: 'role', label: '담당 업무', flex: 1.5 }, { key: 'client', label: '발주처', flex: 2 }, { key: 'tech', label: '사업 내용 또는 관련기술', flex: 3 }]} />
          </>
        )}
      </div>
      {quiz && <MbtiQuiz onClose={() => setQuiz(false)} onApply={(mbti) => { setDraft({ ...draft, mbti }); setQuiz(false); flash(`MBTI를 ${mbti}(으)로 입력했습니다. 저장을 눌러 등록하세요.`); }} />}
    </Modal>
  );
}

/** MBTI 간이 자가 점검 — 20문항 중 끌리는 쪽을 고르면 축별 다수 글자로 유형 판정 */
export function MbtiQuiz({ onClose, onApply }: { onClose: () => void; onApply: (mbti: string) => void }): JSX.Element {
  const [answers, setAnswers] = useState<Array<'a' | 'b' | undefined>>(() => MBTI_QUESTIONS.map(() => undefined));
  const done = answers.filter(Boolean).length;
  const result = done === MBTI_QUESTIONS.length ? scoreMbti(answers as Array<'a' | 'b'>) : '';
  const pick = (i: number, v: 'a' | 'b'): void => setAnswers(answers.map((x, j) => (j === i ? v : x)));
  const opt = (on: boolean): CSSProperties => ({
    flex: 1, textAlign: 'left', fontSize: 12.5, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', lineHeight: 1.4,
    border: `1px solid ${on ? '#2563EB' : '#E5E7EB'}`, background: on ? '#EFF4FE' : '#fff', color: on ? '#1D4ED8' : '#374151', fontWeight: on ? 600 : 400,
  });
  return (
    <ModalFrame title="MBTI 확인하기" width={620} onClose={onClose}
      sub="평소 나와 더 가까운 쪽을 고르세요. 정식 MBTI® 검사가 아닌 간이 자가 점검이라 참고용입니다."
      footer={<>
        <span style={{ flex: 1, fontSize: 12.5, color: '#374151' }}>
          {result ? <><b style={{ fontSize: 15, color: '#1D4ED8' }}>{result}</b> · {MBTI_DESCRIPTIONS[result]}</> : `${done} / ${MBTI_QUESTIONS.length} 문항 응답`}
        </span>
        <button className="dx-btn dx-btn-ghost" onClick={onClose}>취소</button>
        <button className="dx-btn dx-btn-primary" disabled={!result} onClick={() => onApply(result)}>이 결과로 입력</button>
      </>}>
      <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 12 }}>
        {MBTI_QUESTIONS.map((q, i) => (
          <li key={i}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 6 }}>{i + 1}. {q.q}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={opt(answers[i] === 'a')} onClick={() => pick(i, 'a')}>{q.a}</button>
              <button type="button" style={opt(answers[i] === 'b')} onClick={() => pick(i, 'b')}>{q.b}</button>
            </div>
          </li>
        ))}
      </ol>
    </ModalFrame>
  );
}

function Info({ k, v }: { k: string; v?: string }): JSX.Element {
  return (
    <div style={{ background: '#fff', padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center' }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#6B7280', width: 56, flexShrink: 0 }}>{k}</span>
      <span style={{ fontSize: 12.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</span>
    </div>
  );
}

// ─── PDF(인쇄) HTML 생성 — 프로파일 양식 그대로 ───────────────────────
function esc(s?: string): string {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));
}
function buildProfileHtml(basic: ProfileBasic, p: EmployeeProfile, name: string): string {
  const th = (cols: string[]): string => `<tr>${cols.map((c) => `<th>${esc(c)}</th>`).join('')}</tr>`;
  const tr = (cells: string[]): string => `<tr>${cells.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`;
  const section = (title: string, head: string[], body: string): string =>
    `<h3>● ${esc(title)}</h3><table><thead>${th(head)}</thead><tbody>${body || tr(head.map(() => ''))}</tbody></table>`;

  const edu = p.educations.map((e) => tr([e.school, e.period, e.major, e.minor ?? '', e.note ?? ''])).join('');
  const car = p.careers.map((e) => tr([e.company, e.period, e.position, e.duty, e.note ?? ''])).join('');
  const cert = p.certificates.map((e) => tr([e.name, e.issuer, e.acquiredDate, e.note ?? ''])).join('');
  const proj = p.projects.map((e) => tr([e.name, e.period, e.role, e.client, e.tech])).join('');

  return `<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>프로파일_${esc(name)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Malgun Gothic','맑은 고딕',sans-serif; color:#1f2937; margin:28px; font-size:11px; }
  h1 { text-align:center; letter-spacing:8px; font-size:22px; margin:0 0 22px; }
  h3 { font-size:12.5px; margin:18px 0 6px; }
  table { width:100%; border-collapse:collapse; table-layout:fixed; }
  th,td { border:1px solid #b9c2cf; padding:5px 7px; text-align:left; vertical-align:top; word-break:break-word; }
  th { background:#dbe5f1; font-weight:700; text-align:center; }
  .basic td.k { background:#dbe5f1; font-weight:700; text-align:center; width:90px; }
  @media print { body { margin:12mm; } @page { size:A4; margin:12mm; } }
</style></head><body>
  <h1>P R O F I L E</h1>
  <h3>● 기본 정보</h3>
  <table class="basic">
    <tr><td class="k">성 명</td><td>${esc(basic.name)}</td><td class="k">소 속</td><td>${esc(COMPANY)}</td><td class="k">직 위</td><td>${esc(basic.title)}</td></tr>
    <tr><td class="k">생년월일</td><td>${esc(basic.birthday)}</td><td class="k">이 메 일</td><td>${esc(basic.email)}</td><td class="k">연 락 처</td><td>${esc(basic.phone)}</td></tr>
    <tr><td class="k">경력기간</td><td>${esc(p.careerYears)}</td><td class="k">주 소</td><td colspan="3">${esc(basic.address)}</td></tr>
  </table>
  ${section('학력 사항', ['학교명', '재학 기간', '전공', '부전공', '비고'], edu)}
  ${section('근무 경력사항', ['근무처', '근무 기간', '직위', '수행 업무', '비고'], car)}
  ${section('자격증', ['자격증', '발행처', '취득일자', '비고'], cert)}
  ${section('업무 경력사항', ['사업명', '참여 기간', '담당 업무', '발주처', '사업 내용 또는 관련기술'], proj)}
</body></html>`;
}

// ─── Excel(.xls, HTML 테이블) 생성 ──────────────────────────────────
function buildProfileExcelHtml(basic: ProfileBasic, p: EmployeeProfile, name: string): string {
  const tr = (cells: string[], header = false): string =>
    `<tr>${cells.map((c) => `<${header ? 'th' : 'td'} style="border:1px solid #999;padding:4px 8px;${header ? 'background:#dbe5f1;font-weight:bold;text-align:center;' : ''}">${esc(c)}</${header ? 'th' : 'td'}>`).join('')}</tr>`;
  const block = (title: string, head: string[], rows: string[][]): string =>
    `<tr><td colspan="${head.length}" style="font-weight:bold;background:#eef2f7;padding:6px 8px;">● ${esc(title)}</td></tr>` +
    tr(head, true) + (rows.length ? rows.map((r) => tr(r)).join('') : tr(head.map(() => ''))) +
    `<tr><td colspan="${head.length}"></td></tr>`;

  const rows =
    block('기본 정보', ['항목', '내용', '항목', '내용'], [
      ['성명', basic.name, '소속', COMPANY],
      ['직위', basic.title, '생년월일', basic.birthday],
      ['이메일', basic.email, '연락처', basic.phone],
      ['경력기간', p.careerYears ?? '', '주소', basic.address],
    ]) +
    block('학력 사항', ['학교명', '재학 기간', '전공', '부전공', '비고'], p.educations.map((e) => [e.school, e.period, e.major, e.minor ?? '', e.note ?? ''])) +
    block('근무 경력사항', ['근무처', '근무 기간', '직위', '수행 업무', '비고'], p.careers.map((e) => [e.company, e.period, e.position, e.duty, e.note ?? ''])) +
    block('자격증', ['자격증', '발행처', '취득일자', '비고'], p.certificates.map((e) => [e.name, e.issuer, e.acquiredDate, e.note ?? ''])) +
    block('업무 경력사항', ['사업명', '참여 기간', '담당 업무', '발주처', '사업 내용 또는 관련기술'], p.projects.map((e) => [e.name, e.period, e.role, e.client, e.tech]));

  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>${esc(name || '프로파일')}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head><body><table border="1" style="border-collapse:collapse;font-family:'Malgun Gothic';font-size:11px;">${rows}</table></body></html>`;
}
